import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router-dom';
import * as Session from '../pages/Login/Session/Session';
import { log } from '../design-system/utils/console';

import { useB2BPermissions } from '../context/B2BPermissionsContext';
import RouteWithLayout from './RouteWithLayout';
import * as paths from './paths';
import LoaderContent from '../design-system/components/Loader/LoaderContent';
import Layout from '../layout/Main';

// layouts
import publicRoutes from './publicRoutes';

// Aplana el árbol de módulos a rutas registrables (path + component).
// Recorre SIEMPRE los hijos, incluso cuando el nodo actual ya es navegable
// (tiene path + component). Esto evita perder rutas anidadas bajo una hoja
// navegable: si el backend devuelve, p. ej., "Inicio" (/ia) con "PRD"
// (/ia/prd) como descendiente, ambas rutas deben registrarse. Un `return`
// temprano al primer match dejaba fuera a los descendientes.
const getPathItems = (items) => {
    return items.flatMap(item => {
        const self = (item.path && item.component) ? [item] : [];
        const children = item.children ? getPathItems(item.children) : [];
        return [...self, ...children];
    });
};

const Routes = () => {
    const history = useHistory();
    const location = useLocation();

    // Páginas dinámicas desde el endpoint de permisos (ya filtradas por el backend).
    // `permissionsLoading` indica que aún no hay veredicto de permisos (fetch en
    // vuelo o pendiente). Mientras tanto NO evaluamos rutas ni redirects: hacerlo
    // con el árbol vacío mandaría a login a un usuario logueado y provocaría un
    // bucle de redirección. Ver B2BPermissionsContext (fail-closed + loading).
    const { dynamicPages, permissionsLoading } = useB2BPermissions();
    const routes = getPathItems(dynamicPages);
    const defaultRoute = routes.length > 0 ? routes[0].path : paths.LOGIN;
    
    const isLoggedIn = Session.userIsLoggedIn();
    const TMP_LOGIN_START_COOKIE = Session.getCookie(Session.TMP_LOGIN_START_COOKIE);
    const userIsLoggedIn = isLoggedIn || TMP_LOGIN_START_COOKIE ? true : false;
    log(`Routes.jsx Esta o intenta loguearse: ${userIsLoggedIn}`);

    // Rutas públicas (login / auth): NO deben quedar tapadas por el loader ni por
    // la pantalla en blanco, porque Auth/AuthScreen necesitan montarse para
    // completar el login MSAL y redirigir a '/'. La comparación normaliza el slash
    // final (p. ej. '/auth/' debe matchear '/auth'); una comparación estricta
    // dejaba '/auth/' fuera y tapaba el login → no se podía iniciar sesión.
    const normalizePath = (p) => (p && p.length > 1 ? p.replace(/\/+$/, '') : p);
    const currentPathname = normalizePath(location.pathname);
    const isPublicPath = publicRoutes.some(({ path }) => normalizePath(path) === currentPathname);

    // Usuario logueado, en una ruta privada, con permisos aún cargando: mostrar
    // loader y NO evaluar rutas/redirects (evita el bucle de redirección a login
    // mientras llega el veredicto de permisos).
    if (userIsLoggedIn && permissionsLoading && !isPublicPath) {
        return <LoaderContent loading={true} style={{ minHeight: '100vh' }} />;
    }

    // Usuario logueado pero la carga de permisos TERMINÓ y no hay módulos
    // accesibles (el servicio falló, o el usuario no tiene permisos). NO podemos
    // redirigir a /login: la cookie sigue válida y Login.jsx rebotaría a '/', que
    // a su vez vuelve aquí → bucle. Renderizamos el LAYOUT (header + sidebar
    // vacío) con el contenido vacío: el usuario entra a la app autenticado, ve el
    // marco (logo, "Cerrar sesión"), pero sin ningún módulo expuesto (fail-closed
    // sin ciclo). El Sidebar ya hace early-return cuando no hay módulos.
    if (userIsLoggedIn && !permissionsLoading && routes.length === 0 && !isPublicPath) {
        return (
            <Layout>
                <div style={{
                    display: 'flex', flexDirection: 'column',
                    justifyContent: 'center', alignItems: 'center',
                    height: '100%', minHeight: 400, textAlign: 'center',
                    gap: 12, color: '#555',
                }}>
                    <i className="ri-information-line" style={{ fontSize: 56, color: '#9aa0a6' }} />
                    <h2 style={{ margin: 0, fontWeight: 600 }}>No pudimos cargar tus módulos</h2>
                    <p style={{ margin: 0, maxWidth: 460, color: '#777' }}>
                        Ocurrió un problema al cargar la información de tu cuenta.
                        Vuelve a intentarlo en unos minutos. Si el problema persiste,
                        contacta a un administrador.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: 8, padding: '10px 24px', cursor: 'pointer',
                            border: 'none', borderRadius: 6,
                            background: '#4a3aff', color: '#fff', fontSize: 14,
                        }}
                    >
                        Reintentar
                    </button>
                </div>
            </Layout>
        );
    }

    return (
        <Switch>
            {/* Rutas públicas (/login, /auth) PRIMERO. En un Switch gana el primer
                match, así que estas deben evaluarse antes que cualquier catch-all.
                Antes había un <Route path="*"> con Redirect a /login ANTES de estas
                rutas: cuando el usuario no estaba logueado (p. ej. durante el flujo
                MSAL en /auth), ese "*" tapaba /auth y redirigía a /login, que a su
                vez volvía a /auth → bucle /auth↔/login. */}
            {
                publicRoutes.map(({ path, component: Component }, index) => (
                    <Route exact key={index} path={path}
                        render={matchProps => (
                            <Component {...matchProps} history={history} />
                        )}
                    />
                ))
            }
            {userIsLoggedIn ? (
                <>
            <Route path="/" exact render={() => <Redirect to={defaultRoute} />} />
            {
                routes.map((it, index) => (
                    <RouteWithLayout exact key={index} {...it}  />
                ))
            }
                    <Route path="*" render={() => <Redirect to={defaultRoute} />} />
                </>
            ) : (
                // No logueado y no es ruta pública: al login.
                <Route path="*" render={() => <Redirect to={paths.LOGIN} />} />
            )}
        </Switch>
    );
};

export default Routes;
